/**
 * configDrift -- display rules for the field-level config drift view (OP#1783).
 *
 * The server (GET /devices/{serial}/config/drift, services/config_drift) owns the comparison,
 * the operational ignore list and the secret masking; this file only words the result. It
 * never unmasks anything and never decides drift itself, so the panel cannot disagree with the
 * config badge (both read the same persisted verification).
 *
 * Pure (no React, no store, no "@/" runtime imports) so `npm run test:unit` pins it directly.
 */

/** The server's mask placeholder (services/config_redactor.REDACTED). */
export const MASK = "********";

export interface DriftRow {
  path: string;
  label: string;
  desired: unknown;
  actual: unknown;
  note?: string | null;
  status: "drifted";
  masked: boolean;
}

export interface ConfigDrift {
  serial: string;
  verdict: "verified" | "diverged" | "unknown";
  reason: string | null;
  detail: string | null;
  coverage: string | null;
  checked_at: string | null;
  last_checked_at: string | null;
  state_recorded_at: string | null;
  drift_count: number;
  rows: DriftRow[];
  in_sync: { path: string; label: string; kind: string | null }[];
  not_observable: { region: string; label: string }[];
  ignored: { name: string; reason: string }[];
  read_only: true;
  device_online?: boolean;
  stale?: boolean;
}

/**
 * Normalise a drift body: every list defaults to [] and drift_count to the row count. A partial
 * or older response (or an error proxy's empty JSON) must render "not verified", never crash
 * the device sheet -- the sheet carries every other device section too.
 */
export function normaliseDrift(raw: Partial<ConfigDrift> | null | undefined): ConfigDrift {
  const r = raw ?? {};
  const rows = Array.isArray(r.rows) ? r.rows : [];
  return {
    serial: r.serial ?? "",
    verdict: r.verdict === "verified" || r.verdict === "diverged" ? r.verdict : "unknown",
    reason: r.reason ?? null,
    detail: r.detail ?? null,
    coverage: r.coverage ?? null,
    checked_at: r.checked_at ?? null,
    last_checked_at: r.last_checked_at ?? null,
    state_recorded_at: r.state_recorded_at ?? null,
    drift_count: typeof r.drift_count === "number" ? r.drift_count : rows.length,
    rows,
    in_sync: Array.isArray(r.in_sync) ? r.in_sync : [],
    not_observable: Array.isArray(r.not_observable) ? r.not_observable : [],
    ignored: Array.isArray(r.ignored) ? r.ignored : [],
    read_only: true,
    device_online: r.device_online,
    stale: r.stale,
  };
}

/** One desired/actual value as short text: lists joined, objects as compact JSON, masks kept. */
export function formatDriftValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "(not set)";
  if (Array.isArray(value)) {
    if (value.length === 0) return "(none)";
    return value.every((v) => typeof v !== "object" || v === null)
      ? value.map((v) => String(v)).join(", ")
      : JSON.stringify(value);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** True when the value (or anything inside it) is the server's mask. */
export function containsMask(value: unknown): boolean {
  if (value === MASK) return true;
  if (Array.isArray(value)) return value.some(containsMask);
  if (value && typeof value === "object") return Object.values(value).some(containsMask);
  return false;
}

/**
 * The one-line headline above the table. Honest about the three cases a bare badge blurs:
 * drift found, checked and clean, and "could not check" (UNKNOWN never reads as clean).
 */
export function driftHeadline(d: Pick<ConfigDrift, "verdict" | "drift_count" | "in_sync" | "stale">): string {
  const n = d.drift_count;
  if (d.verdict === "unknown") {
    return "Not verified yet: no field-level comparison is available for this device.";
  }
  const stale = d.stale ? " (device offline: last known state)" : "";
  if (n > 0) return `${n} field${n === 1 ? "" : "s"} differ from Meridian's intended config${stale}.`;
  if (d.verdict === "diverged") {
    // Diverged with no field rows: a generation/rejection verdict, not a field difference.
    return `Out of sync, but not in a comparable field: see the reason below${stale}.`;
  }
  const k = d.in_sync.length;
  return k === 1 ? `The one compared field matches${stale}.` : `All ${k} compared fields match${stale}.`;
}
